import {
  formatAmount,
  formatDateTime,
  formatValue,
  getEnumLabel,
} from "~/common/utils"
import { Descriptions, Empty, Flex, Table, Tag, Typography } from "antd"
import type { TableProps } from "antd"
import { WalletRechargeStatus, WalletRechargeType } from "~/common/constants"
import {
  fulfillmentStatusLabel,
  giftStatusColor,
  giftStatusLabel,
  giftTypeColor,
  giftTypeLabel,
  orderSourceLabel,
  orderStatusLabel,
  paymentMethodLabel,
  paymentStatusLabel,
  productCodeLabel,
  rechargeStatusLabel,
  rechargeTypeLabel,
} from "~/pages/Staff/labels"

const { Title } = Typography

const rechargeColumns: TableProps<API.User.WalletRechargeRecord>["columns"] = [
  {
    title: "充值记录ID",
    dataIndex: "id",
    width: 210,
    render: formatValue,
  },
  {
    title: "类型",
    dataIndex: "recharge_type",
    width: 150,
    render: (value: WalletRechargeType) => rechargeTypeLabel[value] || value,
  },
  {
    title: "状态",
    dataIndex: "status",
    width: 100,
    render: (value?: WalletRechargeStatus) =>
      value ? rechargeStatusLabel[value] : "-",
  },
  { title: "充值积点", dataIndex: "recharge_amount", width: 100 },
  { title: "剩余", dataIndex: "remaining_amount", width: 80 },
  { title: "冻结", dataIndex: "frozen_amount", width: 80 },
  { title: "已使用", dataIndex: "used_amount", width: 80 },
  { title: "已退", dataIndex: "refunded_amount", width: 80 },
  {
    title: "来源赠送记录ID",
    dataIndex: "source_staff_gift_op_log_id",
    width: 210,
    render: formatValue,
  },
  {
    title: "来源订单ID",
    dataIndex: "source_order_id",
    width: 210,
    render: formatValue,
  },
  {
    title: "生效时间",
    dataIndex: "effective_at",
    width: 180,
    render: formatDateTime,
  },
  {
    title: "到期时间",
    dataIndex: "expires_at",
    width: 180,
    render: formatDateTime,
  },
]

const RechargeRecords = ({
  records,
  title,
}: {
  records: API.User.WalletRechargeRecord[]
  title: string
}) => {
  return (
    <section>
      <Title level={5}>{title}</Title>
      <Table
        columns={rechargeColumns}
        dataSource={records}
        locale={{ emptyText: "未查询到关联积点充值记录" }}
        pagination={false}
        rowKey={(record) =>
          record.id || `${record.user_id}-${record.effective_at}`
        }
        scroll={{ x: 1_660 }}
        size="small"
      />
    </section>
  )
}

const OperationDetails = ({ log }: { log: API.Staff.StaffGiftOpLog }) => {
  return (
    <section>
      <Title level={5}>赠送操作记录</Title>
      <Descriptions
        bordered
        column={2}
        size="small"
      >
        <Descriptions.Item label="赠送类型">
          <Tag color={giftTypeColor[log.gift_type]}>
            {giftTypeLabel[log.gift_type]}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="赠送状态">
          <Tag color={giftStatusColor[log.gift_status]}>
            {giftStatusLabel[log.gift_status]}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="操作记录ID">
          {formatValue(log.id)}
        </Descriptions.Item>
        <Descriptions.Item label="Staff ID">{log.staff_id}</Descriptions.Item>
        <Descriptions.Item label="用户ID">{log.user_id}</Descriptions.Item>
        <Descriptions.Item label="被赠送手机号">
          {log.user_mobile}
        </Descriptions.Item>
        <Descriptions.Item label="创建时间">
          {formatDateTime(log.created_t)}
        </Descriptions.Item>
        {log.gift_type !== "LEVEL_PACKAGE" ? (
          <>
            <Descriptions.Item label="赠送积点">
              {formatValue(log.wallet_recharge_amount)}
            </Descriptions.Item>
            <Descriptions.Item label="积点到期时间">
              {formatDateTime(log.expires_at)}
            </Descriptions.Item>
            <Descriptions.Item
              label="充值记录ID"
              span={2}
            >
              {formatValue(log.wallet_recharge_record_id)}
            </Descriptions.Item>
            {log.gift_type === "ACTIVITY_GIFT" ? (
              <>
                <Descriptions.Item label="活动编码">
                  {formatValue(log.activity_code)}
                </Descriptions.Item>
                <Descriptions.Item label="抖店订单ID">
                  {formatValue(log.source_doudian_order_id)}
                </Descriptions.Item>
              </>
            ) : null}
          </>
        ) : (
          <>
            <Descriptions.Item label="赠送商品">
              {log.product_code
                ? getEnumLabel(productCodeLabel, log.product_code)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="订单ID">
              {formatValue(log.order_id)}
            </Descriptions.Item>
            <Descriptions.Item
              label="支付记录ID"
              span={2}
            >
              {formatValue(log.payment_trade_id)}
            </Descriptions.Item>
          </>
        )}
        {log.gift_status === "REVOKED" ? (
          <>
            <Descriptions.Item label="撤销 Staff ID">
              {formatValue(log.revoked_by_staff_id)}
            </Descriptions.Item>
            <Descriptions.Item label="撤销时间">
              {formatDateTime(log.revoked_at)}
            </Descriptions.Item>
            <Descriptions.Item
              label="撤销原因"
              span={2}
            >
              {formatValue(log.revoke_reason)}
            </Descriptions.Item>
          </>
        ) : null}
        <Descriptions.Item
          label="备注"
          span={2}
        >
          {formatValue(log.remark)}
        </Descriptions.Item>
      </Descriptions>
    </section>
  )
}

const OrderDetails = ({ order }: { order: API.User.UserOrder | null }) => {
  if (!order) {
    return <Empty description="未查询到关联赠送订单" />
  }

  return (
    <section>
      <Title level={5}>0 元赠送订单</Title>
      <Descriptions
        bordered
        column={3}
        size="small"
      >
        <Descriptions.Item label="订单号">{order.order_no}</Descriptions.Item>
        <Descriptions.Item label="订单ID">
          {formatValue(order.id)}
        </Descriptions.Item>
        <Descriptions.Item label="订单来源">
          {getEnumLabel(orderSourceLabel, order.source)}
        </Descriptions.Item>
        <Descriptions.Item label="商品">
          {getEnumLabel(productCodeLabel, order.product_code)}
        </Descriptions.Item>
        <Descriptions.Item label="订单金额">
          {formatAmount(order.amount)}
        </Descriptions.Item>
        <Descriptions.Item label="订单状态">
          {getEnumLabel(orderStatusLabel, order.status)}
        </Descriptions.Item>
        <Descriptions.Item label="履约状态">
          {getEnumLabel(fulfillmentStatusLabel, order.fulfillment_status)}
        </Descriptions.Item>
        <Descriptions.Item label="结算时间">
          {formatDateTime(order.paid_at)}
        </Descriptions.Item>
        <Descriptions.Item label="关联赠送记录">
          {formatValue(order.source_staff_gift_op_log_id)}
        </Descriptions.Item>
        <Descriptions.Item label="专业版月数">
          {formatValue(order.level_package?.level_month_count)}
        </Descriptions.Item>
        <Descriptions.Item label="权益开始时间">
          {formatDateTime(order.level_package?.level_period_start)}
        </Descriptions.Item>
        <Descriptions.Item label="计划权益结束时间">
          {formatDateTime(order.level_package?.planned_level_period_end)}
        </Descriptions.Item>
        <Descriptions.Item label="实际权益结束时间">
          {formatDateTime(order.level_package?.actual_level_period_end)}
        </Descriptions.Item>
      </Descriptions>
    </section>
  )
}

const PaymentDetails = ({
  payment,
}: {
  payment: API.User.PaymentTrade | null
}) => {
  if (!payment) {
    return <Empty description="未查询到关联赠送支付记录" />
  }

  const giftTrade = payment.payment_method_trades?.STAFF_GIFT

  return (
    <section>
      <Title level={5}>0 元支付交易</Title>
      <Descriptions
        bordered
        column={3}
        size="small"
      >
        <Descriptions.Item label="支付记录ID">
          {formatValue(payment.id)}
        </Descriptions.Item>
        <Descriptions.Item label="订单号">
          {formatValue(payment.order_no)}
        </Descriptions.Item>
        <Descriptions.Item label="订单ID">{payment.order_id}</Descriptions.Item>
        <Descriptions.Item label="支付方式">
          {getEnumLabel(paymentMethodLabel, payment.payment_method)}
        </Descriptions.Item>
        <Descriptions.Item label="支付状态">
          {getEnumLabel(paymentStatusLabel, payment.status)}
        </Descriptions.Item>
        <Descriptions.Item label="交易金额">
          {formatAmount(payment.amount)}
        </Descriptions.Item>
        <Descriptions.Item label="赠送交易流水号">
          {formatValue(giftTrade?.payment_trade_no)}
        </Descriptions.Item>
        <Descriptions.Item label="结算时间">
          {formatDateTime(payment.paid_at)}
        </Descriptions.Item>
        <Descriptions.Item label="创建时间">
          {formatDateTime(payment.created_t)}
        </Descriptions.Item>
      </Descriptions>
    </section>
  )
}

export const GiftDetails = ({
  detail,
}: {
  detail: API.Staff.StaffGiftOpLogDetailResponse
}) => {
  const isPointGift = detail.op_log.gift_type !== "LEVEL_PACKAGE"

  return (
    <Flex
      vertical
      gap={18}
    >
      <OperationDetails log={detail.op_log} />
      {isPointGift ? (
        <RechargeRecords
          records={
            detail.wallet_recharge_record ? [detail.wallet_recharge_record] : []
          }
          title="积点充值记录"
        />
      ) : (
        <>
          <OrderDetails order={detail.order} />
          <PaymentDetails payment={detail.payment_trade} />
          <RechargeRecords
            records={detail.wallet_recharge_records}
            title="专业版月度积点记录"
          />
        </>
      )}
    </Flex>
  )
}
